import { useState } from 'react'

import { ActStrip } from './ActStrip.jsx'
import {
  LOG_MONTH,
  LOG_PRECISION_LABELS,
  MONTH_OPTIONS,
  buildWatchedWhen,
  logButtonLabel,
  logYearOptions,
} from './watchLogWhen.js'
import { KIND_LOG, logCommand, useWatchRequest } from './watchRequests.js'

// V2-4c: "Log it as watched" — the Add panel's own search-result flow
// (`docs/design-briefs/watch/v2/D2D Watch v2.dc.html`'s `results()`/`logSpec`,
// the ONLY place the prototype itself draws this control — see
// `docs/reports/V2-4c.md` for why this slice did not also attach one to the
// title page, despite the brief's own heading naming it).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NO CONFIRM-AFTER-APPLIED HERE, AND THAT IS DELIBERATE
// ═══════════════════════════════════════════════════════════════════════════
//
// Every OTHER control in this codebase that uses `ActStrip`'s Done state
// also calls `useConfirmAfterApplied` — re-reading a fresh projection to
// prove the value it just asked for actually landed, because that same
// projection is what the REST of the page is already showing (a stale
// re-render would look like the press did nothing). This control has no
// such page to reconcile against: a search result comes from `/api/watch/
// search`, a stateless TMDB/AniList lookup with no idea what the bot's own
// catalog contains (`docs/reports/V2-4a.md` §1 — a separate, disjoint
// service). There is nothing rendered elsewhere that a fresh read could
// contradict. `useWatchRequest`'s own poll already does not report APPLIED
// until the bot's applier has genuinely processed the request — showing
// "Logged as watched" at that point is showing the truth, not an
// optimistic guess, which is this repo's actual rule (RECONCILIATION §11),
// not "always re-fetch."
//
// `target` is either an existing subscription id or a provider-qualified
// reference (`tmdb:tv:<id>` / `tmdb:movie:<id>` / `anilist:<id>`,
// `watchRequests.js::logTargetForSearchHit`) — this component treats it as
// an opaque string either way, exactly as the applier does.

function defaultMonthYear(nowMs) {
  const now = new Date(nowMs)
  return { month: String(now.getUTCMonth() + 1).padStart(2, '0'), year: now.getUTCFullYear() }
}

export function LogItControl({ target, title, testId, nowMs = Date.now() }) {
  const [open, setOpen] = useState(false)
  const [precision, setPrecision] = useState(LOG_MONTH)
  const defaults = defaultMonthYear(nowMs)
  const [month, setMonth] = useState(defaults.month)
  const [year, setYear] = useState(defaults.year)
  const request = useWatchRequest()
  const years = logYearOptions(nowMs)

  const whenValue = buildWatchedWhen(precision, month, year).value

  const press = () => {
    request.ask({
      kind: KIND_LOG,
      target,
      payload: { watched_when: buildWatchedWhen(precision, month, year) },
    })
  }

  if (request.status) {
    return (
      <ActStrip
        request={request}
        testId={testId}
        doneText="Logged as watched"
        command={logCommand(title, whenValue)}
        onRetry={() => press()}
      />
    )
  }

  if (!open) {
    return (
      <button
        type="button"
        aria-expanded={false}
        className="d2d-addpanel__logtoggle"
        data-testid={`${testId}-toggle`}
        onClick={() => setOpen(true)}
      >
        Log it as watched
      </button>
    )
  }

  return (
    <div className="d2d-addpanel__logopen" data-testid={`${testId}-open`}>
      <span className="d2d-micro d2d-addpanel__loglabel">When did you finish it?</span>
      <div role="group" aria-label="When did you finish it?" className="d2d-addpanel__logmodes">
        {LOG_PRECISION_LABELS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={precision === value}
            className={`d2d-addpanel__logmode${precision === value ? ' d2d-addpanel__logmode--active' : ''}`}
            data-testid={`${testId}-mode-${value}`}
            onClick={() => setPrecision(value)}
          >
            {label}
          </button>
        ))}
      </div>
      {precision === LOG_MONTH ? (
        <select
          aria-label="Month"
          value={month}
          onChange={(event) => setMonth(event.target.value)}
          className="d2d-addpanel__logselect"
          data-testid={`${testId}-month`}
        >
          {MONTH_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : null}
      {precision !== 'none' ? (
        <select
          aria-label="Year"
          value={year}
          onChange={(event) => setYear(Number(event.target.value))}
          className="d2d-addpanel__logselect"
          data-testid={`${testId}-year`}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      ) : null}
      <div className="d2d-addpanel__logactions">
        <button
          type="button"
          className="d2d-addpanel__logsend"
          data-testid={`${testId}-send`}
          onClick={press}
        >
          {logButtonLabel(precision, month, year)}
        </button>
        <button
          type="button"
          className="d2d-addpanel__logcancel"
          data-testid={`${testId}-cancel`}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export default LogItControl
